/**
 * Recetas médicas veterinarias / ISP — OrangePet
 * -----------------------------------------------------------------------------
 * Implementación 100% nativa (Liquid + Vanilla JS + Cart API de Shopify).
 * No usa apps de terceros.
 *
 * El archivo de la receta se sube como PROPIEDAD DE LÍNEA nativa mediante el
 * `<input type="file" name="properties[Receta Médica]">` que vive dentro del
 * `{% form 'product' %}`. El add-to-cart del tema (product-form.js) envía el
 * formulario con `new FormData(form)`, por lo que el archivo viaja en el POST
 * multipart a /cart/add. Shopify lo almacena en su CDN de Files y guarda la URL
 * como valor de la propiedad, quedando visible en el detalle de la orden.
 *
 * Deliberadamente NO intentamos adjuntar archivos vía /cart/change.js ni
 * /cart/update.js: esos endpoints no aceptan uploads.
 */
(function () {
  'use strict';

  // El script se referencia desde varios snippets; evitamos registrar los
  // listeners globales dos veces si el navegador lo evalúa más de una vez.
  if (window.__recetaMedicaInit) return;
  window.__recetaMedicaInit = true;

  var ATTACHED_CLASS = 'receta-uploader--attached';

  /** Extensiones permitidas derivadas del atributo accept. */
  function parseAccept(accept) {
    return (accept || '.jpg,.jpeg,.png,.pdf')
      .split(',')
      .map(function (value) {
        return value.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function fileExtension(name) {
    var index = name.lastIndexOf('.');
    return index === -1 ? '' : name.slice(index).toLowerCase();
  }

  function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  /* ==========================================================================
   * <receta-uploader> — input de archivo en la página de producto
   * ========================================================================== */
  class RecetaUploaderElement extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-receta-input]');
      this.fileRow = this.querySelector('[data-receta-file]');
      this.fileName = this.querySelector('[data-receta-filename]');
      this.removeButton = this.querySelector('[data-receta-remove]');
      this.errorNode = this.querySelector('[data-receta-error]');

      if (!this.input) return;

      this.allowed = parseAccept(this.dataset.accept);
      this.maxBytes = (parseInt(this.dataset.maxMb, 10) || 20) * 1024 * 1024;

      this.onChange = this.handleChange.bind(this);
      this.onRemove = this.handleRemove.bind(this);
      this.onFormSubmit = this.handleFormSubmit.bind(this);

      this.input.addEventListener('change', this.onChange);
      if (this.removeButton) this.removeButton.addEventListener('click', this.onRemove);

      this.form = this.closest('form') || document.getElementById(this.input.getAttribute('form') || '');
      if (this.form) this.form.addEventListener('submit', this.onFormSubmit, true);

      // product-form.js reactiva el botón de agregar al carro en cada cambio de
      // variante; volvemos a aplicar el bloqueo después de ese repintado.
      this.onVariantChange = function () {
        window.requestAnimationFrame(this.syncState.bind(this));
      }.bind(this);

      this.variantScope = this.closest('.shopify-section, dialog, product-card') || document;
      this.variantScope.addEventListener('variant:update', this.onVariantChange);
      this.variantScope.addEventListener('variant:selected', this.onVariantChange);

      this.syncState();
    }

    disconnectedCallback() {
      if (this.input) this.input.removeEventListener('change', this.onChange);
      if (this.removeButton) this.removeButton.removeEventListener('click', this.onRemove);
      if (this.form) this.form.removeEventListener('submit', this.onFormSubmit, true);
      if (this.variantScope) {
        this.variantScope.removeEventListener('variant:update', this.onVariantChange);
        this.variantScope.removeEventListener('variant:selected', this.onVariantChange);
      }
    }

    get hasFile() {
      return !!(this.input && this.input.files && this.input.files.length > 0);
    }

    handleChange() {
      var file = this.input.files && this.input.files[0];
      if (!file) return this.syncState();

      var extension = fileExtension(file.name);

      if (this.allowed.length && this.allowed.indexOf(extension) === -1) {
        this.reject('Formato no permitido. Adjunta un archivo ' + this.allowed.join(', ') + '.');
        return;
      }

      if (file.size > this.maxBytes) {
        this.reject('El archivo pesa ' + formatSize(file.size) + '. El máximo permitido es ' + this.dataset.maxMb + ' MB.');
        return;
      }

      this.showError('');
      this.syncState();
    }

    reject(message) {
      this.input.value = '';
      this.showError(message);
      this.syncState();
    }

    handleRemove(event) {
      event.preventDefault();
      this.input.value = '';
      this.showError('');
      this.syncState();
    }

    /** Bloquea el envío del formulario si falta la receta. */
    handleFormSubmit(event) {
      if (this.hasFile) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(this.dataset.errorRequired || 'Debes adjuntar la receta médica para continuar.');
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    showError(message) {
      if (!this.errorNode) return;
      this.errorNode.textContent = message || '';
      this.errorNode.hidden = !message;
    }

    syncState() {
      var has = this.hasFile;

      this.dataset.hasFile = has ? 'true' : 'false';
      this.classList.toggle(ATTACHED_CLASS, has);

      if (this.fileRow) this.fileRow.hidden = !has;
      if (this.fileName && has) this.fileName.textContent = this.input.files[0].name;

      this.toggleAddToCart(has);
    }

    /** Deshabilita el botón de agregar al carro mientras no haya receta. */
    toggleAddToCart(enabled) {
      var scope = this.closest('product-form-component') || this.form;
      if (!scope) return;

      var button = scope.querySelector('[ref="addToCartButton"], button[name="add"]');
      if (!button) return;

      if (enabled) {
        if (button.dataset.recetaLocked === 'true') {
          button.disabled = false;
          delete button.dataset.recetaLocked;
        }
      } else {
        button.disabled = true;
        button.dataset.recetaLocked = 'true';
      }
    }
  }

  if (!customElements.get('receta-uploader')) {
    customElements.define('receta-uploader', RecetaUploaderElement);
  }

  /* ==========================================================================
   * <receta-modal> — modal de recetas del carrito
   * ========================================================================== */
  class RecetaModalElement extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.onBackdropClick = this.handleBackdropClick.bind(this);
      this.dialog.addEventListener('click', this.onBackdropClick);

      this.querySelectorAll('[data-receta-close]').forEach(
        function (node) {
          node.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );
    }

    disconnectedCallback() {
      if (this.dialog) this.dialog.removeEventListener('click', this.onBackdropClick);
    }

    open() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('receta-modal-open');
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.documentElement.classList.remove('receta-modal-open');
    }

    /** Cerrar al hacer click fuera del panel. */
    handleBackdropClick(event) {
      if (event.target === this.dialog) this.close();
    }
  }

  if (!customElements.get('receta-modal')) {
    customElements.define('receta-modal', RecetaModalElement);
  }

  /* ==========================================================================
   * Apertura del modal desde el banner (delegación: sobrevive a los morphs)
   * ========================================================================== */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-receta-open]');
    if (!trigger) return;

    event.preventDefault();

    // Prioriza un modal dentro del mismo contenedor (drawer vs página de carrito).
    var scope = trigger.closest('cart-items-component, cart-drawer-component') || document;
    var modal = scope.querySelector('receta-modal') || document.querySelector('receta-modal');

    if (modal && typeof modal.open === 'function') modal.open();
  });

  /* ==========================================================================
   * Quitar una línea con receta desde el modal
   * --------------------------------------------------------------------------
   * Shopify no permite editar una propiedad con archivo sobre una línea que ya
   * está en el carrito, así que "quitar la receta" equivale a quitar la línea.
   * El cliente vuelve al producto y la agrega de nuevo con el archivo correcto.
   * ========================================================================== */
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-receta-remove-line]');
    if (!button) return;

    event.preventDefault();

    var key = button.dataset.recetaRemoveLine;
    if (!key || button.dataset.recetaBusy === 'true') return;

    button.dataset.recetaBusy = 'true';
    button.disabled = true;

    fetch((window.Theme && window.Theme.routes && window.Theme.routes.cart_change_url) || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('No se pudo actualizar el carrito');
        return response.json();
      })
      .then(function () {
        window.location.reload();
      })
      .catch(function (error) {
        console.error('[receta] error al quitar la línea', error);
        button.disabled = false;
        delete button.dataset.recetaBusy;
      });
  });

  /* ==========================================================================
   * Bloqueo del checkout
   * --------------------------------------------------------------------------
   * Liquid ya renderiza el botón con `disabled` cuando falta alguna receta.
   * Esto es la red de seguridad para los repintados por morph del cart drawer.
   * ========================================================================== */
  function enforceCheckoutLock() {
    document.querySelectorAll('[data-receta-gate]').forEach(function (gate) {
      var blocked = gate.dataset.recetaGate === 'blocked';
      var scope = gate.closest('cart-items-component, .cart-page, cart-drawer-component') || document;

      scope.querySelectorAll('button[name="checkout"], [data-receta-checkout]').forEach(function (button) {
        if (blocked) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.dataset.recetaLocked = 'true';
        } else if (button.dataset.recetaLocked === 'true') {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          delete button.dataset.recetaLocked;
        }
      });

      // Los pagos acelerados (Shop Pay, PayPal) saltan el carrito y no arrastran
      // las propiedades de línea con archivo: se ocultan mientras falte receta.
      scope.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach(function (node) {
        node.toggleAttribute('hidden', blocked);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', enforceCheckoutLock);
  document.addEventListener('cart:update', enforceCheckoutLock);
  document.addEventListener('cart:add', enforceCheckoutLock);

  // El cart drawer del tema repinta con morph; observamos el DOM para re-aplicar.
  var observer = new MutationObserver(function () {
    window.requestAnimationFrame(enforceCheckoutLock);
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('cart-items-component').forEach(function (node) {
      observer.observe(node, { childList: true, subtree: true });
    });
    enforceCheckoutLock();
  });

  if (document.readyState !== 'loading') enforceCheckoutLock();
})();
